import type { MovementRow, RegionCode } from '@/types'
import { mapRegionClients, tagRowsWithRegion } from '@/utils/regionApi'
import { useRegionStore } from '@/stores/regionStore'
import { getClientForRegion } from '@/api'

export type LocationScope = 'full_asset_cycle' | 'selected_locations'

export type RfidReportFilters = {
  locations?: string[]
  location?: string
  c2_locations?: string[]
  /** full_asset_cycle uses CONTRACTS_CUSTOMERS_URL; selected_locations skips it */
  location_scope?: LocationScope
  created_at_from?: string
  created_at_to?: string
  sender_code?: string
  receiver_code?: string
  material_code?: string
  material_group_code?: string
  material_group_name?: string
  transfer_type?: string
  rfid_code?: string
  document?: string
  warehouse?: string
  kit_no?: string
  customer_location?: string
  /** yes = draft/in-transit (X); no = normal posted rows */
  in_transit?: 'yes' | 'no'
}

export type ReportSelectOption = { value: string; label: string }

export type RfidReportOptions = {
  locations?: ReportSelectOption[]
  transfer_types: string[]
  warehouses: string[]
  customer_locations: string[]
  c2_locations: string[]
  kit_nos: string[]
  documents: string[]
  material_codes: ReportSelectOption[]
  material_group_codes: ReportSelectOption[]
  material_group_names: string[]
  senders: ReportSelectOption[]
  receivers: ReportSelectOption[]
}

function filenameFromDisposition(header?: string): string | null {
  if (!header) return null
  const utf = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (utf?.[1]) {
    try {
      return decodeURIComponent(utf[1].trim())
    } catch {
      return utf[1].trim()
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header)
  return plain?.[1]?.trim() || null
}

async function readBlobErrorMessage(blob: Blob): Promise<string | null> {
  if (!blob || blob.type.includes('sheet') || blob.type.includes('octet-stream')) return null
  try {
    const text = await blob.text()
    const json = JSON.parse(text) as {
      message?: string
      error?: string
      errors?: Array<{ message?: string } | string>
    }
    if (typeof json.message === 'string') return json.message
    if (typeof json.error === 'string') return json.error
    if (Array.isArray(json.errors) && json.errors.length) {
      return json.errors
        .map((e) => (typeof e === 'string' ? e : e.message || ''))
        .filter(Boolean)
        .join('; ')
    }
  } catch {
    /* not JSON */
  }
  return null
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

function cleanFilters(filters: RfidReportFilters): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(filters)) {
    if (value == null) continue
    if (typeof value === 'string' && !value.trim()) continue
    if (Array.isArray(value) && value.length === 0) continue
    body[key] = Array.isArray(value) ? value : typeof value === 'string' ? value.trim() : value
  }
  return body
}

function uniqStrings(lists: string[][]): string[] {
  return [...new Set(lists.flat().filter(Boolean))].sort()
}

function isCustomerCodeStr(code: string): boolean {
  return /^\d{8}$/.test(code) || /^\d{10}$/.test(code)
}

function uniqOptions(lists: ReportSelectOption[][]): ReportSelectOption[] {
  const map = new Map<string, string>()
  for (const list of lists) {
    for (const o of list || []) {
      if (!o?.value) continue
      const val = String(o.value).trim()
      const label = o.label?.trim() || val
      if (!map.has(val)) {
        map.set(val, label)
      } else {
        const existing = map.get(val)!
        if (!existing.includes('—') && label.includes('—')) {
          map.set(val, label)
        } else if (isCustomerCodeStr(val)) {
          if (existing.toUpperCase().includes('LEAP') && !label.toUpperCase().includes('LEAP')) {
            map.set(val, label)
          }
        }
      }
    }
  }
  return [...map.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

function downloadRegion(): RegionCode {
  const view = useRegionStore.getState().view
  if (view === 'GLOBAL') {
    const available = useRegionStore.getState().availableRegions
    return available[0] || 'IN'
  }
  return view
}

export const reportsService = {
  async getOptions(): Promise<RfidReportOptions> {
    const parts = await mapRegionClients(async ({ client }) => {
      const { data } = await client.get<{ success: boolean; data: RfidReportOptions }>(
        '/rfid_report/options'
      )
      return data.data
    })

    const opts = parts.map((p) => p.result)
    const rawLocations = opts.flatMap((o) => {
      if (o.locations?.length) return o.locations
      const list: ReportSelectOption[] = []
      if (o.senders?.length) list.push(...o.senders)
      if (o.receivers?.length) list.push(...o.receivers)
      if (o.customer_locations?.length) {
        list.push(...o.customer_locations.map((c) => ({ value: c, label: c })))
      }
      if (o.warehouses?.length) {
        list.push(...o.warehouses.map((w) => ({ value: w, label: w })))
      }
      if (o.c2_locations?.length) {
        list.push(...o.c2_locations.map((c) => ({ value: c, label: c })))
      }
      return list
    })

    const unifiedLocations = uniqOptions([rawLocations])

    if (parts.length === 1 && parts[0]!.result.locations?.length) {
      return {
        ...parts[0]!.result,
        locations: uniqOptions([parts[0]!.result.locations]),
      }
    }

    return {
      locations: unifiedLocations,
      transfer_types: uniqStrings(opts.map((o) => o.transfer_types || [])),
      warehouses: uniqStrings(opts.map((o) => o.warehouses || [])),
      customer_locations: uniqStrings(opts.map((o) => o.customer_locations || [])),
      c2_locations: uniqStrings(opts.map((o) => o.c2_locations || [])),
      kit_nos: uniqStrings(opts.map((o) => o.kit_nos || [])),
      documents: uniqStrings(opts.map((o) => o.documents || [])),
      material_codes: uniqOptions(opts.map((o) => o.material_codes || [])),
      material_group_codes: uniqOptions(opts.map((o) => o.material_group_codes || [])),
      material_group_names: uniqStrings(opts.map((o) => o.material_group_names || [])),
      senders: uniqOptions(opts.map((o) => o.senders || [])),
      receivers: uniqOptions(opts.map((o) => o.receivers || [])),
    }
  },

  async preview(
    filters: RfidReportFilters
  ): Promise<{ data: MovementRow[]; total: number; previewLimit: number }> {
    const body = cleanFilters(filters)
    const parts = await mapRegionClients(async ({ client, region }) => {
      const { data } = await client.post<{
        success: boolean
        data: MovementRow[]
        total: number
        previewLimit: number
      }>('/rfid_report/preview', body)
      return {
        data: tagRowsWithRegion(data.data ?? [], region),
        total: data.total ?? 0,
        previewLimit: data.previewLimit ?? 20,
      }
    })

    if (parts.length === 1) return parts[0]!.result

    return {
      data: parts
        .flatMap((p) => p.result.data)
        .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))),
      total: parts.reduce((n, p) => n + p.result.total, 0),
      previewLimit: Math.max(...parts.map((p) => p.result.previewLimit)),
    }
  },

  async downloadRfidReport(filters: RfidReportFilters): Promise<{ filename: string }> {
    const body = cleanFilters(filters)
    // Excel download is single-region (use active region; GLOBAL → primary available)
    const region = downloadRegion()
    const client = getClientForRegion(region)

    try {
      const res = await client.post('/rfid_report', body, {
        responseType: 'blob',
        timeout: 180_000,
      })

      const blob = res.data as Blob
      const errMsg = await readBlobErrorMessage(blob)
      if (errMsg) {
        throw { message: errMsg, statusCode: res.status }
      }

      const filename =
        filenameFromDisposition(res.headers['content-disposition'] as string | undefined) ||
        `rfid_report_${region}_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '_')}.xlsx`

      triggerDownload(blob, filename)
      return { filename }
    } catch (err: unknown) {
      const e = err as {
        message?: string
        statusCode?: number
        response?: { data?: Blob; status?: number }
      }
      if (e.response?.data instanceof Blob) {
        const msg = await readBlobErrorMessage(e.response.data)
        if (msg) throw { message: msg, statusCode: e.response.status || e.statusCode }
      }
      throw err
    }
  },
}
